<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, LatLng, type IconSequence, type PolylineEventMap, type PolylineOptions, type PolylineOverlay } from 'tg-map-core'
import { defineComponent, toRaw, type PropType } from 'vue'
import { eventProp, optionalProp, requiredProp, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import MapMixin from '../map-mixin'

type TgPolylineEmits = {
  'update:path': LatLng[],
}
export default defineComponent({
  name: 'tg-polyline',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: {
    path: requiredProp(Array as PropType<LatLng[]>),
    icons: optionalProp(Array as PropType<IconSequence[]>),
    clickable: optionalProp(Boolean),
    editable: optionalProp(Boolean),
    strokeColor: optionalProp(String),
    strokeOpacity: optionalProp(Number),
    strokeWeight: optionalProp(Number),
    visible: optionalProp(Boolean),
    zIndex: optionalProp(Number),
    'onUpdate:path': eventProp(),
  } satisfies Props<PolylineOptions> & EventProps<TgPolylineEmits>,
  emits: undefined as any as EventEmits<PolylineEventMap & TgPolylineEmits>,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      overlay: lateinit<PolylineOverlay>(),
      emittedPath: undefined as LatLng[] | undefined,
    }
  },
  watch: {
    path: {
      handler(value) {
        if (this.emittedPath != null) {
          const path = this.emittedPath
          this.emittedPath = undefined
          if (path === toRaw(value)) {
            // 防止emit出的path又被设置回去, 导致Google地图移动点后的撤销功能无效
            return
          }
        }
        this.overlay.setPath(value)
      },
      deep: true,
    },
    icons: {
      handler() {
        this.recreate()
      },
      deep: true,
    },
    clickable() {
      this.recreate()
    },
    editable(value) {
      this.overlay.setEditable(value)
    },
    strokeColor(value) {
      this.overlay.setStrokeColor(value)
    },
    strokeOpacity(value) {
      this.overlay.setStrokeOpacity(value)
    },
    strokeWeight(value) {
      this.overlay.setStrokeWeight(value)
    },
    visible(value) {
      this.overlay.setVisible(value)
    },
    zIndex(value) {
      this.overlay.setZIndex(value)
    },
  },
  onCreate() {
    this.overlay = this.$map.createPolyline(this)
    this.$map.addOverlay(this.overlay)
    bindEvents(this.attrs.listeners, this.overlay, this.$emit)

    if (this.attrs.listenerProps['update:path']) {
      this.overlay.addEventListener('path-edited', () => {
        this.$emit('update:path', this.emittedPath = this.overlay.getPath())
      })
    }
  },
  onDestroy() {
    this.overlay.remove()
  },
})
</script>
